import i18nFactory from '@yandex-int/i18n';
import { uniqBy } from 'lodash';
import { Error } from 'tslint/lib/error';

import { Shape2Icon } from '../../components/Shape2/Shape2';
import { banners, campaigns, groups } from '../../typings/common';
import { GridViewType } from '../../typings/grid';
import { RecommendationCharacter, RecommendationType } from '../../typings/recommendations';
import { RecommendationsState } from '../../typings/state';
import { pluralizeWord } from '../Pluralize/Pluralize';

import * as keyset from './RecommendationsHelpers.i18n';

const i18n = i18nFactory(keyset);

// при добавлении нового типа нужно использовать иконки отсюда https://st.yandex-team.ru/DIRECT-85502
// все иконки из задачи уже добавлены в Shape2
export function getRecommendationIconByType(type: RecommendationType): Shape2Icon {
    const dictionary = {
        [RecommendationType.dailyBudget]: Shape2Icon.money,
        [RecommendationType.removePagesFromBlackList]: Shape2Icon.www,
        [RecommendationType.sendLicenses]: Shape2Icon.mail,
        [RecommendationType.changeAdGroupWithLowStat]: Shape2Icon.keywods,
        [RecommendationType.changeAdGroupForModeration]: Shape2Icon.keywods,
        [RecommendationType.switchOnAutotargeting]: Shape2Icon.target,
        [RecommendationType.changeBannerForModeration]: Shape2Icon.banner,
        [RecommendationType.addBannerDisplayHrefs]: Shape2Icon.www,
        [RecommendationType.addTitleExtension]: Shape2Icon.banner,
        [RecommendationType.addSiteLinks]: Shape2Icon.banner,
        [RecommendationType.addAdditionsItemCallouts]: Shape2Icon.banner,
        [RecommendationType.addImageToBanner]: Shape2Icon.image
    };

    if (!dictionary[type]) {
        throw new Error(`Для рекомендации с типом ${type} не определена иконка`);
    }

    return dictionary[type];
}

export function getRecommendationCharacterByType(type: RecommendationType): RecommendationCharacter {
    const dictionary = {
        [RecommendationType.dailyBudget]: RecommendationCharacter.info,
        [RecommendationType.removePagesFromBlackList]: RecommendationCharacter.info,
        [RecommendationType.sendLicenses]: RecommendationCharacter.info,
        [RecommendationType.changeAdGroupWithLowStat]: RecommendationCharacter.info,
        [RecommendationType.changeAdGroupForModeration]: RecommendationCharacter.attention,
        [RecommendationType.changeBannerForModeration]: RecommendationCharacter.attention,
        [RecommendationType.switchOnAutotargeting]: RecommendationCharacter.warning,
        [RecommendationType.addBannerDisplayHrefs]: RecommendationCharacter.info,
        [RecommendationType.addTitleExtension]: RecommendationCharacter.info,
        [RecommendationType.addSiteLinks]: RecommendationCharacter.info,
        [RecommendationType.addAdditionsItemCallouts]: RecommendationCharacter.info,
        [RecommendationType.addImageToBanner]: RecommendationCharacter.info,
        [RecommendationType.addBannerFormats]: RecommendationCharacter.warning,
        [RecommendationType.chooseAppropriatePlacementsForAdGroup]: RecommendationCharacter.attention,
        [RecommendationType.uploadAppropriateCreatives]: RecommendationCharacter.attention,
        [RecommendationType.chooseAppropriatePlacementsForBanner]: RecommendationCharacter.attention
    };

    if (!dictionary[type]) {
        throw new Error(`Для рекомендации с типом ${type} не определена тема`);
    }

    return dictionary[type];
}

export const RecommendationСharacterWeight: Record<RecommendationCharacter, number> = {
    [RecommendationCharacter.info]: 2,
    [RecommendationCharacter.warning]: 2,
    [RecommendationCharacter.attention]: 1
};

export function getRecommendationsWithRefreshErrorStatusMessage(recommendationType: RecommendationType): string {
    switch (recommendationType) {
        case RecommendationType.switchOnAutotargeting:
            return i18n('error-autotargeting-with-refresh');
        case RecommendationType.dailyBudget:
            return i18n('error-campaign-budget-with-refresh');
        case RecommendationType.removePagesFromBlackList:
            return i18n('error-fobidden-pages-with-refresh');
        default:
            return ''; // i18n('error-general-with-refresh');
    }
}

export function getSummaryRecommendationApplyStatusMessage(
    appliedItemsCount: number,
    requestedItemsCount: number,
    recommendationType: RecommendationType
): string {
    const translationKeys = {
        [RecommendationType.switchOnAutotargeting]: 'autotargeting',
        [RecommendationType.dailyBudget]: 'campaign-budget',
        [RecommendationType.removePagesFromBlackList]: 'fobidden-pages'
    };

    const recommendationTypeKey = translationKeys[recommendationType] ? translationKeys[recommendationType] : 'general';

    if (appliedItemsCount === 0) {
        switch (recommendationTypeKey) {
            case RecommendationType.switchOnAutotargeting:
                return i18n('error-autotargeting');
            case RecommendationType.dailyBudget:
                return i18n('error-campaign-budget');
            case RecommendationType.removePagesFromBlackList:
                return i18n('error-fobidden-pages');
            default:
                return ''; // i18n('error-general');
        }
    } else if (appliedItemsCount < requestedItemsCount) {
        switch (recommendationTypeKey) {
            case RecommendationType.switchOnAutotargeting:
                return pluralizeWord(
                    [
                        i18n('one-item-partial-autotargeting-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        }),
                        i18n('some-items-partial-autotargeting-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        }),
                        i18n('many-items-partial-autotargeting-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        })
                    ],
                    requestedItemsCount
                );
            case RecommendationType.dailyBudget:
                return pluralizeWord(
                    [
                        i18n('one-item-partial-campaign-budget-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        }),
                        i18n('some-items-partial-campaign-budget-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        }),
                        i18n('many-items-partial-campaign-budget-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        })
                    ],
                    requestedItemsCount
                );
            case RecommendationType.removePagesFromBlackList:
                return pluralizeWord(
                    [
                        i18n('one-item-partial-fobidden-pages-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        }),
                        i18n('some-items-partial-fobidden-pages-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        }),
                        i18n('many-items-partial-fobidden-pages-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        })
                    ],
                    requestedItemsCount
                );
            default:
                return pluralizeWord(
                    [
                        i18n('one-item-partial-general-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        }),
                        i18n('some-items-partial-general-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        }),
                        i18n('many-items-partial-general-result', {
                            count: appliedItemsCount,
                            total: requestedItemsCount
                        })
                    ],
                    requestedItemsCount
                );
        }
    } else {
        switch (recommendationTypeKey) {
            case RecommendationType.switchOnAutotargeting:
                return pluralizeWord(
                    [
                        i18n('one-item-full-autotargeting-result', {
                            count: appliedItemsCount
                        }),
                        i18n('some-items-full-autotargeting-result', {
                            count: appliedItemsCount
                        }),
                        i18n('many-items-full-autotargeting-result', {
                            count: appliedItemsCount
                        })
                    ],
                    appliedItemsCount
                );
            case RecommendationType.dailyBudget:
                return pluralizeWord(
                    [
                        i18n('one-item-full-campaign-budget-result', {
                            count: appliedItemsCount
                        }),
                        i18n('some-items-full-campaign-budget-result', {
                            count: appliedItemsCount
                        }),
                        i18n('many-items-full-campaign-budget-result', {
                            count: appliedItemsCount
                        })
                    ],
                    appliedItemsCount
                );
            case RecommendationType.removePagesFromBlackList:
                return pluralizeWord(
                    [
                        i18n('one-item-full-fobidden-pages-result', {
                            count: appliedItemsCount
                        }),
                        i18n('some-items-full-fobidden-pages-result', {
                            count: appliedItemsCount
                        }),
                        i18n('many-items-full-fobidden-pages-result', {
                            count: appliedItemsCount
                        })
                    ],
                    appliedItemsCount
                );
            default:
                return pluralizeWord(
                    [
                        i18n('one-item-full-general-result', {
                            count: appliedItemsCount
                        }),
                        i18n('some-items-full-general-result', {
                            count: appliedItemsCount
                        }),
                        i18n('many-items-full-general-result', {
                            count: appliedItemsCount
                        })
                    ],
                    appliedItemsCount
                );
        }
    }
}

export function getSummaryRecommendationApplyStatusCount(
    recommendationType: RecommendationType,
    requestedKeys: Array<{ cid: string | null }>,
    enabledForApplyKeys: Array<{ cid: string | null }>
): {
    appliedItemsCount: number;
    requestedItemsCount: number;
} {
    let appliedItemsCount, requestedItemsCount;

    if (recommendationType === RecommendationType.removePagesFromBlackList) {
        appliedItemsCount = uniqBy(enabledForApplyKeys, 'cid').length;
        requestedItemsCount = uniqBy(requestedKeys, 'cid').length;
    } else {
        appliedItemsCount = enabledForApplyKeys.length;
        requestedItemsCount = requestedKeys.length;
    }

    return {
        appliedItemsCount,
        requestedItemsCount
    };
}

export function getContextRecommendationApplyStatusMessage(
    recommendationType: RecommendationType,
    itemName: string
): string {
    switch (recommendationType) {
        case RecommendationType.switchOnAutotargeting:
            return i18n('autotargeting-context-result', {
                name: itemName
            });
        case RecommendationType.dailyBudget:
            return i18n('campaign-budget-context-result', {
                name: itemName
            });
        case RecommendationType.removePagesFromBlackList:
            return i18n('fobidden-pages-context-result', {
                name: itemName
            });
        default:
            return i18n('general-context-result', {
                name: itemName
            });
    }
}

export function getContextRecommendationErrorStatusMessage(
    recommendationType: RecommendationType,
    itemName: string
): string {
    switch (recommendationType) {
        case RecommendationType.switchOnAutotargeting:
            return i18n('autotargeting-context-result-error', {
                name: itemName
            });
        case RecommendationType.dailyBudget:
            return i18n('campaign-budget-context-result-error', {
                name: itemName
            });
        case RecommendationType.removePagesFromBlackList:
            return i18n('fobidden-pages-context-result-error', {
                name: itemName
            });
        default:
            return i18n('general-context-result-error', {
                name: itemName
            });
    }
}

export function getContextRecommendationWithRefreshErrorStatusMessage(
    recommendationType: RecommendationType,
    itemName: string
): string {
    switch (recommendationType) {
        case RecommendationType.switchOnAutotargeting:
            return i18n('autotargeting-context-result-error-with-refresh', {
                name: itemName
            });
        case RecommendationType.dailyBudget:
            return i18n('campaign-budget-context-result-error-with-refresh', {
                name: itemName
            });
        case RecommendationType.removePagesFromBlackList:
            return i18n('fobidden-pages-context-result-error-with-refresh', {
                name: itemName
            });
        default:
            return ''; /* i18n('general-context-result-error-with-refresh', {
                name: itemName
            }); */
    }
}

export function getGridRecommendationApplyStatusMessage(
    recommendationType: RecommendationType,
    itemName: string
): string {
    switch (recommendationType) {
        case RecommendationType.switchOnAutotargeting:
            return i18n('autotargeting-grid-result', {
                name: itemName
            });
        case RecommendationType.dailyBudget:
            return i18n('campaign-budget-grid-result', {
                name: itemName
            });
        case RecommendationType.removePagesFromBlackList:
            return i18n('fobidden-pages-grid-result', {
                name: itemName
            });
        default:
            return i18n('general-grid-result', {
                name: itemName
            });
    }
}

export function getGridRecommendationErrorStatusMessage(
    recommendationType: RecommendationType,
    itemName: string
): string {
    switch (recommendationType) {
        case RecommendationType.switchOnAutotargeting:
            return i18n('autotargeting-grid-result-error', {
                name: itemName
            });
        case RecommendationType.dailyBudget:
            return i18n('campaign-budget-grid-result-error', {
                name: itemName
            });
        case RecommendationType.removePagesFromBlackList:
            return i18n('fobidden-pages-grid-result-error', {
                name: itemName
            });
        default:
            return i18n('general-grid-result-error', {
                name: itemName
            });
    }
}

export function getGridRecommendationWithRefreshErrorStatusMessage(
    recommendationType: RecommendationType,
    itemName: string
): string {
    switch (recommendationType) {
        case RecommendationType.switchOnAutotargeting:
            return i18n('autotargeting-grid-result-error-with-refresh', {
                name: itemName
            });
        case RecommendationType.dailyBudget:
            return i18n('campaign-budget-grid-result-error-with-refresh', {
                name: itemName
            });
        case RecommendationType.removePagesFromBlackList:
            return i18n('fobidden-pages-grid-result-error-with-refresh', {
                name: itemName
            });
        default:
            return ''; /* i18n('general-grid-result-error-with-refresh', {
                name: itemName
            }); */
    }
}

export function getSummaryRecommendationConfirmMessage(
    recommendationType: RecommendationType,
    itemsLength: number
): string {
    switch (recommendationType) {
        case RecommendationType.switchOnAutotargeting:
            return pluralizeWord(
                [
                    i18n('are-you-sure-to-apply-one-groups', {
                        count: itemsLength
                    }),
                    i18n('are-you-sure-to-apply-some-groups', {
                        count: itemsLength
                    }),
                    i18n('are-you-sure-to-apply-many-groups', {
                        count: itemsLength
                    })
                ],
                itemsLength
            );
        case RecommendationType.dailyBudget:
        case RecommendationType.removePagesFromBlackList:
            return pluralizeWord(
                [
                    i18n('are-you-sure-to-apply-one-campaigns', {
                        count: itemsLength
                    }),
                    i18n('are-you-sure-to-apply-some-campaigns', {
                        count: itemsLength
                    }),
                    i18n('are-you-sure-to-apply-many-campaigns', {
                        count: itemsLength
                    })
                ],
                itemsLength
            );
        default:
            throw `Unsupported ${recommendationType} for confirm message`;
    }
}

export const recommendationsInitialState: RecommendationsState = {
    recommendationsData: undefined,
    error: undefined,
    recommendationsReqId: ''
};

export function getRecommendationIdContext(type: GridViewType, id: string): string {
    switch (type) {
        case campaigns:
            return i18n('campaign', { id });
        case groups:
            return i18n('group', { id });
        case banners:
            return i18n('banner', { id });
        default:
            return '';
    }
}

export const getRecommendationAgregatorTitle = (count: number, view: GridViewType): string => {
    switch (view) {
        case campaigns:
            return i18n('campaign-title', { count });

        case groups:
            return i18n('group-title', { count });

        case banners:
            return i18n('banner-title', { count });

        default:
            return i18n('title', { count });
    }
};
